import { Button } from '@mui/material'
import { Labeled, TextInput, DateTimeInput } from 'ra-ui-materialui'
import React from 'react'
import { FileField, FormDataConsumer, TextField, useNotify } from 'react-admin'
import { MappedEnrichedEvent } from 'services/api/entities/events/types/internal/event-models'
import { eventStatusChoices, paymentMethodChoices } from 'services/cfg/form'
import { countrySwitchStrict } from 'services/geo-management'
import { FormDataConsumerProps } from 'types/common'
import { PaymentMethod } from 'types/form'
import CustomizationsArrayInput from 'ui-shared/FormComponents/CustomizationsArrayInput'
import EventServiceBriefSection from 'ui-shared/FormComponents/EventServiceBriefSection'
import Kdivider from 'ui-shared/LayoutComponents/Kdivider'
import KFileInput from 'ui-shared/WrappedInputs/KFileInput'
import KNumberInput from 'ui-shared/WrappedInputs/KNumberInput'
import { useFormContext } from 'react-hook-form'
import KFormTab from 'ui-shared/WrappedInputs/KFormTab'
import { getStoreFrontBaseUrlByEnvironment } from '../utils'
import KSelectInput from 'ui-shared/WrappedInputs/KSelectInput'

type Props = {
  resource?: string
  children?: React.ReactNode
}

export type EventFormDataConsumerProps =
  FormDataConsumerProps<MappedEnrichedEvent>

const SF_BASE_URL = getStoreFrontBaseUrlByEnvironment()
const BASE_EVENT_ROUTE = countrySwitchStrict({
  IT: `${SF_BASE_URL}/event`,
  SE: `${SF_BASE_URL}/sk/event`,
})

const GeneralTab: React.FC<Props> = (props) => {
  const { children } = props

  const eventStatusDefault =
    props.resource === 'events'
      ? eventStatusChoices[0].id
      : eventStatusChoices[3].id

  const isDraft = props.resource === 'eventsdraft'

  const filteredPaymentMethodChoices = isDraft
    ? paymentMethodChoices
    : [paymentMethodChoices[0], paymentMethodChoices[1]]

  const { getValues } = useFormContext()
  const notify = useNotify()

  const copyEventLink = () => {
    const link = `${BASE_EVENT_ROUTE}/${getValues('id')}`

    navigator.clipboard.writeText(link)
    notify('notification.info.linkCopiedToClipboard', { type: 'info' })
  }

  return (
    <KFormTab {...props} label="form.tabs.general">
      <Button
        variant="contained"
        style={{ position: 'absolute', right: '2.5em' }}
        onClick={copyEventLink}
      >
        Copy Event Link
      </Button>

      {children}
      <KSelectInput
        source="eventStatus"
        label="form.fields.eventStatus"
        choices={eventStatusChoices}
        defaultValue={eventStatusDefault}
      />
      <FormDataConsumer>
        {({
          formData,
        }: FormDataConsumerProps<{
          id: number
          paymentMethod: PaymentMethod
        }>) =>
          formData.paymentMethod === 'organizationBudget' ? (
            <Labeled label="form.fields.paymentMethod">
              <TextField source="paymentMethod" record={formData} />
            </Labeled>
          ) : (
            <KSelectInput
              source="paymentMethod"
              label="form.fields.paymentMethod"
              defaultValue={
                isDraft
                  ? paymentMethodChoices[2].id
                  : paymentMethodChoices[0].id
              }
              choices={filteredPaymentMethodChoices}
            />
          )
        }
      </FormDataConsumer>

      <DateTimeInput source="startDate" label="form.fields.startDate" />
      <DateTimeInput source="endDate" label="form.fields.endDate" />

      <KNumberInput source="guestsNumber" label="form.fields.guests" />
      <TextInput source="fullAddress" label="form.fields.address" />
      {/* TODO: here we should check mandatoriety of fullAddress when event is not virtual */}

      <CustomizationsArrayInput />

      <KFileInput
        accept="application/pdf,.xls,.xlsx,.docx"
        source="kampaayerDocs"
        multiple
      />

      <Kdivider title="Client Docs" />

      <FileField
        source="customerDocs"
        src="src"
        title="title"
        target="_blank"
      />

      <FormDataConsumer>
        {({ formData }: EventFormDataConsumerProps) =>
          !!formData?.generalService?.briefQuestions?.length && (
            <>
              <Kdivider title="Event Brief" headingLevel="h3" />
              <EventServiceBriefSection serviceSource="generalService" />
            </>
          )
        }
      </FormDataConsumer>
    </KFormTab>
  )
}

export default GeneralTab
